import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, DOCUMENT,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input, NgZone,
  OnDestroy,
  Output, PLATFORM_ID, Renderer2,
  ViewChild
} from '@angular/core';
import {CommonModule, isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'app-shared-policy-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './shared-policy-modal.component.html',
})
export class SharedPolicyModalComponent implements OnDestroy, AfterViewInit {
  @Input() title = 'Policy';
  @Input() contentHtml = '';                 // (optional) use if your content is static HTML
  @Input() contentTpl: any | null = null;    // (optional) pass a TemplateRef from parent

  @Output() closed = new EventEmitter<void>();
  @Output() scrolledToEnd = new EventEmitter<void>();
  @Output() agreed = new EventEmitter<void>();
  @ViewChild('dialog') dialogRef!: ElementRef<HTMLElement>;
  @ViewChild('modalBody') modalBodyRef!: ElementRef<HTMLElement>;

  open = false;
  hasScrolledToEnd = false;
  requiresScroll = true;
  labelId = `policy-modal-${Math.random().toString(36).slice(2)}`;
  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngAfterViewInit(): void {
    // no-op here; focusing is triggered when opening
  }

  // Amount of overflow (px) below which we treat the content as "not scrollable".
  private static readonly SCROLL_THRESHOLD = 20;

  /**
   * Measure the modal body once it has been laid out. Runs via requestAnimationFrame
   * and retries until the browser reports real dimensions (clientHeight > 0), so the
   * check happens reliably right after the popup appears.
   *
   * If the content fits without meaningful overflow, the "I Agree" button is enabled
   * immediately — no scrolling or clicking inside the popup required.
   */
  private scheduleFitCheck(attempt: number): void {
    requestAnimationFrame(() => {
      if (!this.open || this.hasScrolledToEnd) return;

      const el = this.modalBodyRef?.nativeElement;
      if (el && el.clientHeight > 0) {
        const overflow = el.scrollHeight - el.clientHeight;
        if (overflow <= SharedPolicyModalComponent.SCROLL_THRESHOLD) {
          this.markReachedEnd();
        }
        // Otherwise the content requires scrolling; the (scroll) handler takes over.
        return;
      }

      // Layout not ready yet — retry on the next frame (bounded).
      if (attempt < 30) {
        this.scheduleFitCheck(attempt + 1);
      }
    });
  }

  /** Mark that the user has reached the end of the agreement (enables the button). */
  private markReachedEnd(): void {
    if (this.hasScrolledToEnd) return;
    this.requiresScroll = false;
    this.hasScrolledToEnd = true;
    this.scrolledToEnd.emit();
    // Force a change-detection pass. This app runs without zone.js, so programmatic
    // updates (from rAF/timers) don't auto-refresh the view like template events do.
    this.cdr.detectChanges();
  }

  openModal(): void {
    this.open = true;
    this.hasScrolledToEnd = false;
    this.requiresScroll = true;

    if (this.isBrowser) {
      // Trap scroll on body (use Renderer2 for safety)
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');

      // Focus after the dialog is in the DOM
      // Use NgZone + setTimeout to avoid "changed after checked" in some cases
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          // dialogRef may be undefined right after open in some cases
          const node = this.dialogRef?.nativeElement;
          node?.focus?.();
        }, 0);
      });

      // Measure as soon as the popup appears. If the agreement fits without scrolling,
      // "I Agree" is enabled immediately; otherwise the (scroll) handler takes over.
      this.scheduleFitCheck(0);
    }
  }

  close(): void {
    this.open = false;
    this.closed.emit();

    if (this.isBrowser) {
      // Restore body scroll
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }

  onAgree(): void {
    if (!this.hasScrolledToEnd) return;
    this.agreed.emit();
    this.close();
  }

  onModalBodyScroll(event: Event): void {
    if (this.hasScrolledToEnd) return;

    const el = event.target as HTMLElement;
    // Consider "scrolled to end" when within the threshold of the bottom
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SharedPolicyModalComponent.SCROLL_THRESHOLD) {
      this.hasScrolledToEnd = true;
      this.scrolledToEnd.emit();
    }
  }

  onBackdrop(e: MouseEvent): void {
    // Close only if the click is on the backdrop (element with class 'modal')
    const target = e.target as HTMLElement | null;
    if (target?.classList?.contains('modal')) {
      this.close();
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (!this.open || !this.isBrowser) return;

    if (e.key === 'Tab') {
      const dialogEl = this.dialogRef?.nativeElement;
      if (!dialogEl) return;

      // Keep focus within the dialog (basic focus trap)
      const focusables = dialogEl.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = this.document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && active === last) {
        first.focus();
        e.preventDefault();
      }
    } else if (e.key === 'Escape') {
      // Nice-to-have: ESC closes modal
      this.close();
    }
  }


  ngOnDestroy(): void {
    // Ensure body style cleanup only in browser
    if (this.isBrowser) {
      this.renderer.removeStyle(this.document.body, 'overflow');
    }
  }
}
